import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { Edit, Renew, TrashCan } from '@carbon/icons-react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../../components/states';
import { Money, When } from '../../components/format';
import { CommandStatus } from '../../components/labels';
import { Board, LODE_ACTION, PRIZE_NAME, PRIZES, REGION } from '../../components/LodeChange';
import type { Command } from '../Commands';
import { DrawState, SOURCE_REASON, SOURCE_STATUS, todayVn } from './common';

type BoardT = Record<string, string>;
type DrawRow = {
  cityId: number; name: string; region: string; date: string; state: string; closeAt: string; drawnAt: string;
  special: string | null; source: 'manual' | 'sources' | null; confirmedAt: string | null;
  sourceStatus: { status: string; reason: string | null; error: string | null; at: string; minhNgoc: boolean; xskt: boolean } | null;
  cancelled: { reason: string | null; at: string } | null;
  tickets: { open: number; openStake: number; settled: number; settledStake: number; payout: number; refunded: number };
  pending: { id: string; status: string; action: string; maker: string | null; at: string }[];
};
type DrawList = { date: string; isoDate: string; draws: DrawRow[] };

export function LodeDrawsPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayVn();
  const region = params.get('region') ?? '';
  const q = useQuery({
    queryKey: ['lode-draws', date, region],
    queryFn: () => api.get<DrawList>('/api/admin/lode/draws', { date, ...(region ? { region } : {}) }),
    refetchInterval: 30_000,
  });
  const set = (k: string, v: string) => setParams((p) => { const o = new URLSearchParams(p); if (v) o.set(k, v); else o.delete(k); return o; });
  const rows = q.data?.draws ?? [];
  const need = rows.filter((d) => d.state === 'qua_han' || d.state === 'lech' || (d.state === 'cho_nguon' && d.sourceStatus?.reason === 'hai nguon lech'));
  return (
    <div className="page">
      <PageHeader title="Kỳ quay lô đề"
        lead="Các đài quay trong ngày theo miền, kết quả và trạng thái hai nguồn (Minh Ngọc, XSKT). Khi hai nguồn lệch hoặc không về, nhập kết quả tay; lệnh thực hiện ngay, không chờ người duyệt: hệ A ghi và chốt vé." />
      <div className="toolbar-filters" role="group" aria-label="Chọn ngày và miền">
        <div><TextInput id="lode-date" type="date" labelText="Ngày quay" value={date} onChange={(e) => set('date', e.target.value)} /></div>
        <div>
          <Select id="lode-region" labelText="Miền" value={region} onChange={(e) => set('region', e.target.value)}>
            <SelectItem value="" text="Cả ba miền" />
            {Object.entries(REGION).map(([k, t]) => <SelectItem key={k} value={k} text={t} />)}
          </Select>
        </div>
        <span className="toolbar-filters__grow" />
        <Button kind="ghost" size="md" renderIcon={Renew} onClick={() => q.refetch()} disabled={q.isFetching}>Tải lại</Button>
      </div>
      {need.length ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title={`${need.length} đài cần xem`}
          subtitle={need.map((d) => d.name).join(', ')} />
      ) : null}
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !rows.length ? (
        <EmptyState title="Không có đài quay" body="Không đài nào quay vào ngày và miền đã chọn." />
      ) : (
        <table className="plain vis">
          <thead><tr><th>Đài</th><th>Trạng thái</th><th>Giải đặc biệt</th><th>Nguồn</th><th className="r">Vé chờ</th><th className="r">Tiền cược chờ</th><th className="r">Vé đã chốt</th><th className="r">Đã trả</th><th>Lệnh đang chờ</th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.cityId} className="row--click" onClick={() => nav(`/lode/draws/${q.data!.isoDate}/${d.cityId}`)}>
                <td className="is-title"><Link className="cell-link" to={`/lode/draws/${q.data!.isoDate}/${d.cityId}`} onClick={(e) => e.stopPropagation()}>{d.name}</Link>
                  <div className="muted">{REGION[d.region]}, chốt nhận cược <When v={d.closeAt} /></div></td>
                <td data-label="Trạng thái"><DrawState s={d.state} /></td>
                <td data-label="Giải đặc biệt" className="num">{d.special ?? '-'}{d.source === 'manual' ? <div className="muted">nhập tay</div> : null}</td>
                <td data-label="Nguồn">{d.sourceStatus ? <>{SOURCE_STATUS[d.sourceStatus.status] ?? d.sourceStatus.status}
                  {d.sourceStatus.reason ? <div className="muted">{SOURCE_REASON[d.sourceStatus.reason] ?? d.sourceStatus.reason}</div> : null}</> : <span className="muted">-</span>}</td>
                <td className="r num" data-label="Vé chờ">{d.tickets.open}</td>
                <td className="r" data-label="Tiền cược chờ"><Money v={d.tickets.openStake} /></td>
                <td className="r num" data-label="Vé đã chốt">{d.tickets.settled}{d.tickets.refunded ? <div className="muted">{d.tickets.refunded} hoàn</div> : null}</td>
                <td className="r" data-label="Đã trả"><Money v={d.tickets.payout} /></td>
                <td data-label="Lệnh đang chờ">{d.pending.length ? d.pending.map((c) => (
                  <div key={c.id}><Link className="cell-link" to={`/commands/${c.id}`} onClick={(e) => e.stopPropagation()}>{LODE_ACTION[c.action] ?? c.action}</Link> <CommandStatus s={c.status} /></div>
                )) : <span className="muted">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

type DrawDetail = {
  date: string; isoDate: string; cityId: number; name: string; region: string; state: string;
  shape: Record<string, [number, number]>; board: BoardT | null; source: 'manual' | 'sources' | null; laterSources: BoardT | null;
  sources: { status: string; reason: string | null; error: string | null; at: string; minhNgoc: BoardT | null; xskt: BoardT | null } | null;
  cancelled: { reason: string | null; at: string; maker: string | null; approver: string | null } | null;
  history: { id: number; kind: string; before: BoardT | null; after: BoardT | null; beforeSource: string | null; reason: string; maker: string | null; approver: string | null; at: string }[];
  adjustments: { kind: string; tickets: number; refunded: number; delta: number; applied: number; debt: number; debtRepaid: number }[];
  byType: { code: string; status: string; tickets: number; stake: number; payout: number }[];
  commands: { id: string; status: string; action: string; reason: string; maker: string | null; approver: string | null; at: string; error: string | null }[];
};

type Action = 'nhap_kq' | 'chot_lai' | 'huy';
const ADJ_KIND: Record<string, string> = { chot_lai: 'Chốt lại', huy: 'Hủy kỳ', hoan_ve: 'Hoàn vé' };

function BoardForm({ shape, value, onChange }: { shape: Record<string, [number, number]>; value: BoardT; onChange: (b: BoardT) => void }) {
  return (
    <div className="lode-board-form">
      {PRIZES.filter((g) => (shape[g]?.[0] ?? 0) > 0).map((g) => {
        const [n, digits] = shape[g]!;
        const list = (value[g] ?? '').split(/[-,\s]+/).filter(Boolean);
        const bad = list.length > 0 && (list.length !== n || list.some((s) => s.length !== digits || !/^\d+$/.test(s)));
        return (
          <TextInput key={g} id={'kq-' + g} labelText={`${PRIZE_NAME[g]} (${n} số, ${digits} chữ số)`} value={value[g] ?? ''} inputMode="numeric"
            placeholder={Array.from({ length: n }, () => '0'.repeat(digits)).join(' ')}
            invalid={bad} invalidText={`Cần ${n} số, mỗi số ${digits} chữ số, cách nhau bằng dấu cách`}
            onChange={(e) => onChange({ ...value, [g]: e.target.value.replace(/[^\d\s,-]/g, '') })} />
        );
      })}
    </div>
  );
}

const boardReady = (shape: Record<string, [number, number]>, b: BoardT) => PRIZES.every((g) => {
  const [n, digits] = shape[g] ?? [0, 0];
  const list = (b[g] ?? '').split(/[-,\s]+/).filter(Boolean);
  return list.length === n && list.every((s) => s.length === digits && /^\d+$/.test(s));
});
const spaced = (b: BoardT | null) => Object.fromEntries(PRIZES.map((g) => [g, (b?.[g] ?? '').split('-').filter(Boolean).join(' ')]));

export function LodeDrawPage() {
  const { date = '', cityId = '' } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lode-draw', date, cityId], queryFn: () => api.get<DrawDetail>(`/api/admin/lode/draws/${date}/${cityId}`) });
  const [action, setAction] = useState<Action | null>(null);
  const [board, setBoard] = useState<BoardT>({});
  const [reason, setReason] = useState('');
  const [made, setMade] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>(`/api/admin/lode/draws/${date}/${cityId}`, { action, reason, ...(action === 'huy' ? {} : { board }) }),
    onSuccess: (r) => { setMade(r.command); setAction(null); qc.invalidateQueries({ queryKey: ['lode-draw'] }); qc.invalidateQueries({ queryKey: ['lode-draws'] }); },
  });
  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const d = q.data;
  const open = d.commands.filter((c) => c.status === 'AWAITING_APPROVAL' || c.status === 'PENDING');
  const drawn = d.state !== 'mo_cuoc' && d.state !== 'dang_quay';
  const may = can('lode.manage') && !d.cancelled && !open.length;
  const start = (a: Action, from: BoardT | null) => { m.reset(); setReason(''); setBoard(spaced(from)); setAction(a); };
  const bestSource = d.sources?.minhNgoc ?? d.sources?.xskt ?? null;
  const byStatus = (s: string[]) => d.byType.filter((b) => s.includes(b.status));
  const sum = (rows: DrawDetail['byType'], f: 'tickets' | 'stake' | 'payout') => rows.reduce((x, r) => x + r[f], 0);

  return (
    <div className="page">
      <PageHeader title={`${d.name}, ngày ${d.date}`} lead={<>{REGION[d.region]} <DrawState s={d.state} /></>}
        actions={<>
          <Link className="cell-link" to="/lode">← Danh sách kỳ quay</Link>
          {may ? <>
          {!d.board && drawn ? <Button kind="primary" renderIcon={Edit} onClick={() => start('nhap_kq', bestSource)}>Nhập kết quả</Button> : null}
          {d.board ? <Button kind="tertiary" renderIcon={Edit} onClick={() => start('chot_lai', d.laterSources ?? d.board)}>Chốt lại kết quả</Button> : null}
          <Button kind="danger--tertiary" renderIcon={TrashCan} onClick={() => start('huy', null)}>Hủy kỳ</Button>
          </> : null}
        </>} />
      {made ? (
        <ActionableNotification kind={made.status === 'FAILED' ? 'error' : 'info'} inline lowContrast onClose={() => setMade(null)}
          title={made.status === 'FAILED' ? 'Hệ A từ chối lệnh' : made.status === 'SUCCEEDED' ? 'Hệ A đã thực hiện lệnh' : 'Đã gửi lệnh, hệ A đang thực hiện'}
          subtitle="Mở lệnh để xem kết quả hệ A trả về." actionButtonLabel="Mở lệnh" onActionButtonClick={() => nav(`/commands/${made.id}`)} />
      ) : null}
      {open[0] ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton title="Kỳ này có lệnh đang chờ"
          subtitle={`${LODE_ACTION[open[0].action] ?? open[0].action}. Không tạo lệnh mới cho tới khi lệnh này xong.`}
          actionButtonLabel="Mở lệnh" onActionButtonClick={() => nav(`/commands/${open[0]!.id}`)} />
      ) : null}
      {d.cancelled ? (
        <InlineNotification kind="error" lowContrast hideCloseButton title="Kỳ đã hủy"
          subtitle={`${d.cancelled.reason ?? ''} (${d.cancelled.at})`} />
      ) : null}
      <div className="split">
        <section className="section section--flush">
          <h2>Kết quả đang dùng{d.source === 'manual' ? ' (nhập tay)' : d.source === 'sources' ? ' (hai nguồn khớp)' : ''}</h2>
          {d.board ? <Board board={d.board} shape={d.shape} /> : <p className="muted">Chưa có kết quả.</p>}
          {d.laterSources ? <>
            <h3 className="lode-h3">Hai nguồn đổi sang bảng này sau khi chốt</h3>
            <Board board={d.laterSources} shape={d.shape} />
          </> : null}
        </section>
        <section className="section section--flush">
          <h2>Nguồn</h2>
          {d.sources ? <>
            <dl className="facts">
              <dt>Trạng thái</dt><dd>{SOURCE_STATUS[d.sources.status] ?? d.sources.status}</dd>
              {d.sources.reason ? <><dt>Lý do</dt><dd>{SOURCE_REASON[d.sources.reason] ?? d.sources.reason}</dd></> : null}
              {d.sources.error ? <><dt>Lỗi đọc nguồn</dt><dd>{d.sources.error}</dd></> : null}
              <dt>Lúc đọc</dt><dd><When v={d.sources.at} /></dd>
            </dl>
            {d.sources.minhNgoc ? <><h3 className="lode-h3">Minh Ngọc</h3><Board board={d.sources.minhNgoc} shape={d.shape} /></> : null}
            {d.sources.xskt ? <><h3 className="lode-h3">XSKT</h3><Board board={d.sources.xskt} shape={d.shape} /></> : null}
          </> : <p className="muted">Hệ A chưa đọc nguồn cho đài này.</p>}
        </section>
      </div>
      <div className="section">
        <h2>Vé theo loại cược</h2>
        {d.byType.length ? (
          <table className="plain">
            <thead><tr><th>Loại</th><th>Trạng thái</th><th className="r">Vé</th><th className="r">Tiền cược</th><th className="r">Đã trả</th></tr></thead>
            <tbody>{d.byType.map((b) => (
              <tr key={b.code + b.status}><td className="is-title">{b.code}</td><td data-label="Trạng thái">{b.status}</td><td className="r num" data-label="Vé">{b.tickets}</td>
                <td className="r" data-label="Tiền cược"><Money v={b.stake} /></td><td className="r" data-label="Đã trả"><Money v={b.payout} /></td></tr>
            ))}</tbody>
            <tfoot><tr><td>Tổng</td><td>{sum(byStatus(['cho']), 'tickets')} vé chờ</td><td className="r num">{sum(d.byType, 'tickets')}</td>
              <td className="r"><Money v={sum(d.byType, 'stake')} /></td><td className="r"><Money v={sum(d.byType, 'payout')} /></td></tr></tfoot>
          </table>
        ) : <p className="muted">Chưa có vé nào.</p>}
      </div>
      {d.adjustments.length ? (
        <div className="section">
          <h2>Tiền đã điều chỉnh</h2>
          <table className="plain">
            <thead><tr><th>Việc</th><th className="r">Vé</th><th className="r">Hoàn cược</th><th className="r">Chênh thưởng</th><th className="r">Đã vào ví</th><th className="r">Trừ vào nợ cũ</th><th className="r">Ghi nợ</th></tr></thead>
            <tbody>{d.adjustments.map((a) => (
              <tr key={a.kind}><td className="is-title">{ADJ_KIND[a.kind] ?? a.kind}</td><td className="r num" data-label="Vé">{a.tickets}</td>
                <td className="r" data-label="Hoàn cược"><Money v={a.refunded} /></td><td className="r" data-label="Chênh thưởng"><Money v={a.delta} signed /></td>
                <td className="r" data-label="Đã vào ví"><Money v={a.applied} signed /></td><td className="r" data-label="Trừ vào nợ cũ"><Money v={a.debtRepaid} /></td><td className="r" data-label="Ghi nợ"><Money v={a.debt} /></td></tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      <Modal open={action !== null} size={action === 'huy' ? 'sm' : 'md'} danger={action === 'huy'}
        modalHeading={action === 'nhap_kq' ? 'Nhập kết quả tay' : action === 'chot_lai' ? 'Chốt lại kết quả' : 'Hủy kỳ và hoàn vé'}
        primaryButtonText="Thực hiện ngay" secondaryButtonText="Đóng"
        primaryButtonDisabled={m.isPending || reason.trim().length < 5 || (action !== 'huy' && !boardReady(d.shape, board))}
        onRequestClose={() => setAction(null)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          {action === 'huy' ? (
            <p>Mọi vé chưa hoàn của {d.name} ngày {d.date} được hoàn tiền cược; vé đã trả thưởng bị thu lại phần thưởng (thiếu tiền thì ghi nợ). Sau khi hủy, kỳ không nhận kết quả hay cược nữa.</p>
          ) : action === 'chot_lai' ? (
            <p>Mọi vé đã chốt được tính lại theo bảng mới; ví chỉ đổi phần chênh. Bảng điền sẵn là bảng {d.laterSources ? 'hai nguồn đổi sang' : 'đang dùng'}.</p>
          ) : (
            <p>Bảng điền sẵn là của {d.sources?.minhNgoc ? 'Minh Ngọc' : d.sources?.xskt ? 'XSKT' : 'không nguồn nào'}. Kiểm tra với kết quả chính thức trước khi gửi.</p>
          )}
          {action && action !== 'huy' ? <BoardForm shape={d.shape} value={board} onChange={setBoard} /> : null}
          <TextArea id="lode-reason" labelText="Lý do" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            invalid={reason.length > 0 && reason.trim().length < 5} invalidText="Ít nhất 5 ký tự" />
          <InlineError error={m.error} />
        </div>
      </Modal>
    </div>
  );
}
